import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, User, Building, Gift, Check, Sparkles, ArrowRight, ArrowLeft, Globe, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LanguageSelector from '../../components/ui/LanguageSelector';
import { affiliateService } from '../../services/affiliateService';
import api from '../../services/api';

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string;
  description_en: string;
  price_monthly: string;
  price_yearly: string;
  price_usd_monthly: string;
  price_usd_yearly: string;
  features: Record<string, any>;
  limits: Record<string, number>;
  is_featured: boolean;
  trial_days: number;
  sort_order: number;
}

const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralInfo, setReferralInfo] = useState<{ affiliate_name?: string } | null>(null);

  // Plan selection
  const [step, setStep] = useState<'plan' | 'form'>('plan');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Load plans on mount
  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/subscription/plans');
      if (response.data.success) {
        // Filter out custom plan and sort by sort_order
        const filteredPlans = response.data.data
          .filter((p: Plan) => p.slug !== 'custom')
          .sort((a: Plan, b: Plan) => a.sort_order - b.sort_order);
        setPlans(filteredPlans);

        // Pre-select featured plan or first paid plan
        const featuredPlan = filteredPlans.find((p: Plan) => p.is_featured);
        const firstPaidPlan = filteredPlans.find((p: Plan) => parseFloat(p.price_monthly) > 0);
        setSelectedPlan(featuredPlan || firstPaidPlan || filteredPlans[0]);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      affiliateService.checkReferralCode(ref).then((response) => {
        if (response.success && response.data?.valid) {
          setReferralInfo({ affiliate_name: response.data.affiliate_name });
        }
      });
    }

    // Check for plan in URL (accepts either plan id or slug)
    const planParam = searchParams.get('plan');
    if (planParam && plans.length > 0) {
      const urlPlan = plans.find(p => p.id === planParam || p.slug === planParam);
      if (urlPlan) {
        setSelectedPlan(urlPlan);
        setStep('form');
      }
    }

    // Check for billing cycle in URL
    const cycleParam = searchParams.get('cycle');
    if (cycleParam === 'monthly' || cycleParam === 'yearly') {
      setBillingCycle(cycleParam);
    }
  }, [searchParams, plans]);

  const schema = z.object({
    first_name: z.string().min(1, t('errors.required')),
    last_name: z.string().min(1, t('errors.required')),
    email: z.string().email(t('errors.invalidEmail')),
    phone: z.string().min(8, t('errors.required')).max(20),
    company_name: z.string().optional(),
    password: z.string().min(8, t('errors.passwordMin')),
    confirm_password: z.string()
  }).refine((data) => data.password === data.confirm_password, {
    message: t('errors.passwordMatch'),
    path: ['confirm_password']
  });

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  const onSubmit = async (data: FormData) => {
    if (!selectedPlan) {
      toast.error(t('auth.selectPlanFirst', 'Please select a plan first'));
      setStep('plan');
      return;
    }

    setIsLoading(true);
    try {
      const response = await registerUser({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        company_name: data.company_name,
        language: i18n.language as 'pt' | 'en',
        referral_code: referralCode || undefined,
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle
      } as any);

      toast.success(t('messages.createdSuccessfully'));

      // If paid plan selected, redirect to Stripe checkout
      const isPaidPlan = parseFloat(selectedPlan.price_monthly) > 0;
      if (isPaidPlan && response?.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else {
        // Free plan or no checkout needed, go to onboarding
        navigate('/onboarding');
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (plan: Plan) => {
    const isEnglish = i18n.language === 'en';

    // Use USD prices for English, BRL for Portuguese
    let price;
    if (isEnglish) {
      price = billingCycle === 'yearly'
        ? parseFloat(plan.price_usd_yearly || '0') / 12
        : parseFloat(plan.price_usd_monthly || '0');
    } else {
      price = billingCycle === 'yearly'
        ? parseFloat(plan.price_yearly) / 12
        : parseFloat(plan.price_monthly);
    }

    if (price === 0) return t('pricing.free', 'Free');

    return new Intl.NumberFormat(isEnglish ? 'en-US' : 'pt-BR', {
      style: 'currency',
      currency: isEnglish ? 'USD' : 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const getTrialText = (plan: Plan) => {
    if (parseFloat(plan.price_monthly) === 0) return null;
    return t('pricing.trialDays', '7 days free trial');
  };

  const getPlanFeatures = (plan: Plan) => {
    const features: string[] = [];
    const limits = plan.limits;

    if (limits.campaigns === -1) {
      features.push(t('pricing.unlimitedCampaigns', 'Unlimited campaigns'));
    } else {
      features.push(t('pricing.campaignsCount', '{{count}} campaigns', { count: limits.campaigns }));
    }

    if (limits.keywords === -1) {
      features.push(t('pricing.unlimitedKeywords', 'Unlimited keywords'));
    } else {
      features.push(t('pricing.keywordsCount', '{{count}} keywords', { count: limits.keywords }));
    }

    if (limits.team_members === -1) {
      features.push(t('pricing.unlimitedTeam', 'Unlimited team members'));
    } else if (limits.team_members > 1) {
      features.push(t('pricing.teamCount', '{{count}} team members', { count: limits.team_members }));
    }

    if (plan.features.ai_content) features.push(t('pricing.aiContent', 'AI Content Generation'));
    if (plan.features.competitor_analysis) features.push(t('pricing.competitorAnalysis', 'Competitor Analysis'));
    if (plan.features.api_access || limits.api_access) features.push(t('pricing.apiAccess', 'API Access'));

    return features.slice(0, 5);
  };

  // Step 1: Plan Selection - Full screen overlay
  if (step === 'plan') {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 overflow-y-auto">
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/devskin-logo.png" alt="DevSkin" className="w-10 h-10 rounded-lg" />
                <span className="text-xl font-bold text-gray-900 dark:text-white">DevSkin Marketing</span>
              </div>
              <div className="flex items-center gap-4">
                <LanguageSelector />
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">
                  {t('auth.hasAccount')} {t('auth.signInNow')}
                </Link>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t('auth.choosePlan', 'Choose your plan')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                {t('auth.trialInfo', 'Start with 7 days free trial. No credit card required for Free plan.')}
              </p>
            </div>

            {/* Billing Toggle */}
            <div className="flex justify-center mb-10">
              <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-xl inline-flex shadow-inner">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-8 py-3 rounded-lg text-sm font-semibold transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white dark:bg-gray-700 shadow-md text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {t('pricing.monthly', 'Monthly')}
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-8 py-3 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    billingCycle === 'yearly'
                      ? 'bg-white dark:bg-gray-700 shadow-md text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {t('pricing.yearly', 'Yearly')}
                  <span className="text-green-600 text-xs font-bold bg-green-100 px-2 py-1 rounded-full">
                    {t('pricing.save', 'Save')} 17%
                  </span>
                </button>
              </div>
            </div>

            {/* Plans Grid */}
            {loadingPlans ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-8">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-[480px] bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-8">
                {plans.map((plan) => {
                  const isSelected = selectedPlan?.id === plan.id;
                  const isPaid = parseFloat(plan.price_monthly) > 0;

                  return (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`relative cursor-pointer rounded-2xl border-2 p-8 transition-all duration-200 flex flex-col bg-white dark:bg-gray-800 ${
                        isSelected
                          ? 'border-primary-500 shadow-2xl shadow-primary-500/20 scale-[1.02]'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:shadow-xl'
                      } ${plan.is_featured ? 'ring-2 ring-primary-500 ring-offset-4' : ''}`}
                    >
                      {plan.is_featured && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <span className="bg-gradient-to-r from-primary-500 to-purple-500 text-white text-sm font-bold px-5 py-2 rounded-full flex items-center gap-2 shadow-lg">
                            <Sparkles className="w-4 h-4" />
                            {t('pricing.popular', 'Popular')}
                          </span>
                        </div>
                      )}

                      {isSelected && (
                        <div className="absolute top-4 right-4">
                          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center shadow-lg">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}

                      <div className="text-center mb-8 pt-2">
                        <h3 className="font-bold text-2xl text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm min-h-[40px]">
                          {i18n.language === 'pt' ? plan.description : plan.description_en}
                        </p>
                        <div className="mt-6">
                          <span className="text-5xl font-bold text-gray-900 dark:text-white">
                            {formatPrice(plan)}
                          </span>
                          {isPaid && (
                            <span className="text-gray-500 dark:text-gray-400 text-lg">/{t('pricing.month', 'mo')}</span>
                          )}
                        </div>
                        {billingCycle === 'yearly' && isPaid && (
                          <p className="text-gray-400 text-sm mt-1">
                            {new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'pt-BR', {
                              style: 'currency',
                              currency: i18n.language === 'en' ? 'USD' : 'BRL',
                              minimumFractionDigits: 0
                            }).format(parseFloat(i18n.language === 'en' ? (plan.price_usd_yearly || '0') : plan.price_yearly))}/{t('pricing.year', 'year')}
                          </p>
                        )}
                        {getTrialText(plan) && (
                          <p className="text-green-600 text-sm mt-3 font-semibold bg-green-50 py-1 px-3 rounded-full inline-block">
                            {getTrialText(plan)}
                          </p>
                        )}
                      </div>

                      <ul className="space-y-4 flex-grow">
                        {getPlanFeatures(plan).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlan(plan);
                          setStep('form');
                        }}
                        className={`mt-8 w-full py-3 px-6 rounded-xl font-semibold transition-all ${
                          isSelected
                            ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {isPaid ? t('pricing.startTrial', 'Start Free Trial') : t('pricing.getStarted', 'Get Started')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom CTA */}
            <div className="mt-12 text-center">
              <Button
                onClick={() => setStep('form')}
                disabled={!selectedPlan}
                size="lg"
                className="px-12 py-4 text-lg shadow-xl"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                {t('auth.continueWithPlan', 'Continue with {{plan}}', { plan: selectedPlan?.name || '' })}
              </Button>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
                {t('pricing.cancelAnytime', 'Cancel anytime. No questions asked.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration Form
  return (
    <div>
      <button
        type="button"
        onClick={() => setStep('plan')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('auth.changePlan', 'Change plan')}
      </button>

      {/* Selected Plan Summary */}
      {selectedPlan && (
        <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('auth.selectedPlan', 'Selected plan')}</p>
              <div className="flex items-center gap-2">
                <p className="font-bold text-gray-900 dark:text-white">{selectedPlan.name}</p>
                {parseFloat(selectedPlan.price_monthly) > 0 && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    billingCycle === 'yearly'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  }`}>
                    {billingCycle === 'yearly'
                      ? t('pricing.yearly', 'Anual')
                      : t('pricing.monthly', 'Mensal')}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-primary-600">
                {formatPrice(selectedPlan)}
                {parseFloat(selectedPlan.price_monthly) > 0 && (
                  <span className="text-gray-500 dark:text-gray-400 text-sm font-normal">/{t('pricing.month', 'mo')}</span>
                )}
              </p>
              {billingCycle === 'yearly' && parseFloat(selectedPlan.price_monthly) > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'pt-BR', {
                    style: 'currency',
                    currency: i18n.language === 'en' ? 'USD' : 'BRL',
                    minimumFractionDigits: 0
                  }).format(parseFloat(i18n.language === 'en' ? (selectedPlan.price_usd_yearly || '0') : selectedPlan.price_yearly))}
                  {' '}{t('pricing.billedYearly', 'cobrado anualmente')}
                </p>
              )}
              {parseFloat(selectedPlan.price_monthly) > 0 && (
                <p className="text-xs text-green-600">{t('pricing.trialDays', '7 days free trial')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {t('auth.createAccount')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {t('auth.hasAccount')}{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          {t('auth.signInNow')}
        </Link>
      </p>

      {/* Referral Banner */}
      {referralInfo && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Gift className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                {t('auth.referred_by', { name: referralInfo.affiliate_name })}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                {t('auth.referral_welcome')}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('auth.firstName')}
            placeholder="João"
            leftIcon={<User className="w-5 h-5" />}
            error={errors.first_name?.message}
            {...register('first_name')}
          />
          <Input
            label={t('auth.lastName')}
            placeholder="Silva"
            error={errors.last_name?.message}
            {...register('last_name')}
          />
        </div>

        <Input
          label={t('auth.email')}
          type="email"
          placeholder="email@exemplo.com"
          leftIcon={<Mail className="w-5 h-5" />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label={t('auth.phone', 'Celular')}
          type="tel"
          placeholder="+55 (11) 99999-9999"
          leftIcon={<Phone className="w-5 h-5" />}
          error={(errors as any).phone?.message}
          {...register('phone')}
        />

        <Input
          label={t('auth.company')}
          placeholder="Empresa (opcional)"
          leftIcon={<Building className="w-5 h-5" />}
          error={errors.company_name?.message}
          {...register('company_name')}
        />

        <Input
          label={t('auth.password')}
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock className="w-5 h-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <Input
          label={t('auth.confirmPassword')}
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock className="w-5 h-5" />}
          error={errors.confirm_password?.message}
          {...register('confirm_password')}
        />

        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('auth.termsAgree')}{' '}
          <a
            href="https://marketing.devskin.com/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700"
          >
            {t('auth.terms')}
          </a>{' '}
          {t('auth.and')}{' '}
          <a
            href="https://marketing.devskin.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700"
          >
            {t('auth.privacy')}
          </a>
        </p>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          isLoading={isLoading}
        >
          {parseFloat(selectedPlan?.price_monthly || '0') > 0
            ? t('auth.startTrial', 'Start Free Trial')
            : t('auth.register')
          }
        </Button>
      </form>
    </div>
  );
};

export default RegisterPage;
